import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Alert from '@material-ui/lab/Alert';
import CookService from '../../services/cook.service';
import PlannedMealLink from './PlannedMealLink.component';
import { fmtDuration, fmtTemp, fmtWeight, fmtFoodType, STATUS_LABELS } from './cookFormat';

const useStyles = makeStyles((theme) => ({
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: theme.spacing(2), margin: '8px 0' },
  controls: { display: 'flex', alignItems: 'flex-end', gap: theme.spacing(2), flexWrap: 'wrap' },
  filter: { minWidth: 180 },
  row: { cursor: 'pointer' },
  muted: { color: '#888' },
  completed: { color: '#2e7d32' },
  ended_early: { color: '#c62828' },
  in_progress: { color: '#74653F', fontWeight: 'bold' }
}));

const CookListPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const [cooks, setCooks] = useState(null);
  const [foodTypes, setFoodTypes] = useState([]);
  const [foodType, setFoodType] = useState('');
  const [message, setMessage] = useState(null); // {severity,text}

  const apiErr = err => err.response?.data?.message || 'Request failed.';

  useEffect(() => {
    CookService.foodTypes().then(res => setFoodTypes(res.data)).catch(() => {});
  }, []);

  const load = () => CookService.list(foodType)
    .then(res => setCooks(res.data))
    .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));

  useEffect(() => { load(); }, [foodType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aggregate every analysed cook into per-food-type priors for cookcast.
  const rebuildPriors = () => {
    CookService.rebuildPriors()
      .then(res => setMessage({ severity: 'success',
        text: `Priors rebuilt for ${res.data.length} food type${res.data.length === 1 ? '' : 's'}.` }))
      .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));
  };

  return (
    <>
      <div className={classes.header}>
        <Typography variant="h6">BBQ Cooks</Typography>
        <div className={classes.controls}>
          <FormControl className={classes.filter} size="small">
            <InputLabel id="cook-food-type">Food type</InputLabel>
            <Select labelId="cook-food-type" value={foodType} onChange={e => setFoodType(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {foodTypes.map(t => <MenuItem key={t} value={t}>{fmtFoodType(t)}</MenuItem>)}
            </Select>
          </FormControl>
          <Button size="small" color="primary" variant="outlined" onClick={rebuildPriors}>Rebuild priors</Button>
        </div>
      </div>

      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} style={{ marginBottom: 8 }}>{message.text}</Alert>}

      {cooks && cooks.length === 0 &&
        <Typography variant="body2" className={classes.muted} style={{ margin: 8 }}>
          No cooks logged{foodType ? ` for ${fmtFoodType(foodType)}` : ''} yet. They appear here
          as soon as the cookcast service on the BBQ starts one.
        </Typography>}

      {cooks && cooks.length > 0 &&
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Food type</TableCell>
                <TableCell>Cut</TableCell>
                <TableCell align="right">Weight</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell align="right">Target</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Planned meal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cooks.map(c => (
                <TableRow key={c.id} hover className={classes.row} onClick={() => history.push(`/cooks/${c.id}`)}>
                  <TableCell>{new Date(c.startedAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</TableCell>
                  <TableCell>{fmtFoodType(c.foodType)}</TableCell>
                  <TableCell>{c.cut || '—'}</TableCell>
                  <TableCell align="right">{fmtWeight(c.weightG)}</TableCell>
                  <TableCell align="right">{fmtDuration(c.durationS)}</TableCell>
                  <TableCell align="right">{fmtTemp(c.targetC)}</TableCell>
                  <TableCell className={classes[c.status]}>
                    {STATUS_LABELS[c.status]}{c.outcome ? ` · ${c.outcome}` : ''}
                  </TableCell>
                  <TableCell>
                    <PlannedMealLink compact cookId={c.id} plannedMeal={c.plannedMeal} onChange={load} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>}
    </>
  );
};

export default CookListPage;
